/**
 * React Query hook wrapping `fetchInterviewerSchedule`.
 *
 * Cache key is `(weekStartIso, normalizedTz, sorted interviewer ids)`, so:
 *   - rearranging the interviewer list does not re-fetch;
 *   - switching tz variants (Calcutta vs Kolkata) does not re-fetch;
 *   - two different weeks hit two different cache lines.
 */
import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import {
  fetchInterviewerSchedule,
  type FetchInterviewerScheduleArgs,
} from './fetchInterviewerSchedule';
import type { InterviewerScheduleByUserId } from './types';

export interface UseInterviewerScheduleQueryArgs extends FetchInterviewerScheduleArgs {
  enabled?: boolean;
}

export function buildInterviewerScheduleQueryKey(
  weekStartIso: string,
  timezone: string,
  sortedIds: number[]
): readonly unknown[] {
  return [
    'interviewer-schedule',
    weekStartIso,
    normalizeTimeZoneForAtsCalendarApi(timezone),
    sortedIds.join(','),
  ] as const;
}

export function useInterviewerScheduleQuery({
  weekStartIso,
  timezone,
  interviewerIds,
  enabled = true,
}: UseInterviewerScheduleQueryArgs): UseQueryResult<
  InterviewerScheduleByUserId,
  Error
> {
  const sortedIds = [...interviewerIds]
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);

  const isEnabled =
    Boolean(enabled) &&
    sortedIds.length > 0 &&
    Boolean(weekStartIso) &&
    timezone !== 'local';

  const query = useQuery({
    queryKey: buildInterviewerScheduleQueryKey(
      weekStartIso,
      timezone,
      sortedIds
    ),
    queryFn: () =>
      fetchInterviewerSchedule({
        weekStartIso,
        timezone,
        interviewerIds: sortedIds,
      }),
    enabled: isEnabled,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      console.warn('[interviewer-schedule] error', query.error);
    }
  }, [query.isError, query.error]);

  return query;
}
